import React from 'react';
import { DateTime } from 'luxon';
import DatePicker from '@amzn/meridian/date-picker';
import InputGroup from '@amzn/meridian/input-group';
import TimePicker from '@amzn/meridian/time-picker';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper/useTranslation';

export interface ScheduleInputGroupProps {
  startTime: string;
  endTime: string;
  testIdPrefix?: string;
  onStartTimeChange: (iso: string) => void;
  onEndTimeChange: (iso: string) => void;
}

function getTestId (testIdPrefix: string | undefined, testId: string): string {
  return testIdPrefix ? `${testIdPrefix}-${testId}` : testId;
}

export const parseIso = (iso: string): { date: string; time: string } => {
  if (!iso) return { date: '', time: '' };
  const dt = DateTime.fromISO(iso, { zone: 'utc' });
  if (!dt.isValid) return { date: '', time: '' };
  return { date: dt.toFormat('yyyy-MM-dd'), time: dt.toFormat('HH:mm:ss') };
};

export const toIso = (date: string, time: string): string => {
  if (!date || !time) return '';
  const dt = DateTime.fromISO(`${date}T${time}`, { zone: 'utc' });
  if (!dt.isValid) return '';
  return dt.toISO() ?? '';
};

export const ScheduleInputGroup: React.FC<ScheduleInputGroupProps> = ({
  startTime,
  endTime,
  testIdPrefix,
  onStartTimeChange,
  onEndTimeChange,
}) => {
  const { t } = useTranslation();
  const start = parseIso(startTime);
  const end = parseIso(endTime);

  return (
    <Row alignmentVertical="bottom" spacing="400" wrap="down">
      <Column spacing="100">
        <Text color="secondary" type="b200">{t('asset_schedule_goes_live_utc')}</Text>
        <InputGroup>
          <DatePicker
            data-testid={getTestId(testIdPrefix, 'schedule-start-date')}
            label="Date"
            value={start.date}
            onChange={(date: string) => { onStartTimeChange(toIso(date, start.time || '00:00:00')); }}
          />
          <TimePicker
            data-testid={getTestId(testIdPrefix, 'schedule-start-time')}
            label="Time"
            value={start.time}
            onChange={(time: string) => { onStartTimeChange(toIso(start.date, time)); }}
          />
        </InputGroup>
      </Column>
      <Column spacing="100">
        <Text color="secondary" type="b200">{t('asset_schedule_taken_down_utc')}</Text>
        <InputGroup>
          <DatePicker
            data-testid={getTestId(testIdPrefix, 'schedule-end-date')}
            label="Date"
            value={end.date}
            onChange={(date: string) => { onEndTimeChange(toIso(date, end.time || '00:00:00')); }}
          />
          <TimePicker
            data-testid={getTestId(testIdPrefix, 'schedule-end-time')}
            label="Time"
            value={end.time}
            onChange={(time: string) => { onEndTimeChange(toIso(end.date, time)); }}
          />
        </InputGroup>
      </Column>
    </Row>
  );
};
